/**
 * Cookie banner for public marketing routes only.
 *
 * Mounted inside the public-route Suspense boundary so the banner does not
 * paint while a lazy marketing chunk is still loading (Bug #1388).
 * Defers visibility by one animation frame (double rAF) after mount so header,
 * body, and footer paint before the fixed consent chrome on eager `/` as well.
 */

import { useLayoutEffect, useState } from 'react';
import { useConsent } from '../../contexts/ConsentContext';
import { CookieBanner } from './CookieBanner';

/**
 * Schedules visibility on the next paint using double requestAnimationFrame.
 * Exported for unit tests that assert defer timing without mounting the banner.
 */
export function scheduleDeferredCookieBannerVisible(onVisible: () => void): () => void {
  let cancelled = false;
  let outerId = 0;
  let innerId = 0;

  outerId = requestAnimationFrame(() => {
    innerId = requestAnimationFrame(() => {
      if (!cancelled) {
        onVisible();
      }
    });
  });

  return () => {
    cancelled = true;
    cancelAnimationFrame(outerId);
    cancelAnimationFrame(innerId);
  };
}

/**
 * Renders {@link CookieBanner} only after this component mounts (lazy route
 * committed) and the defer schedule completes.
 */
export const DeferredPublicCookieBanner: React.FC = () => {
  const { isConsentSet } = useConsent();
  const [deferComplete, setDeferComplete] = useState(false);

  useLayoutEffect(() => {
    if (isConsentSet) {
      setDeferComplete(false);
      return undefined;
    }

    setDeferComplete(false);
    return scheduleDeferredCookieBannerVisible(() => {
      setDeferComplete(true);
    });
  }, [isConsentSet]);

  if (isConsentSet || !deferComplete) {
    return null;
  }

  return <CookieBanner />;
};
